import { HttpClient } from "@angular/common/http";
import { Injectable, Optional } from "@angular/core";
import {
	ChangeSet,
	ChangeSetItem,
	changeSetItemFactory,
	DefaultDataServiceConfig,
	EntityCacheDataService,
	EntityDefinitionService,
} from "@ngrx/data";
import { ApiPayload, ApiResponse, RecordTypeName } from "@recore-solutions/nstar-lib";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

@Injectable()
export class NstarCacheDataService extends EntityCacheDataService {
	constructor(
		protected entityDefinitionService: EntityDefinitionService,
		protected http: HttpClient,
		@Optional() config?: DefaultDataServiceConfig
	) {
		super(entityDefinitionService, http, config);
	}

	saveEntities(changeSet: ChangeSet<RecordTypeName<any>>, url: string): Observable<ChangeSet> {
		// Begin just like super:
		changeSet = this.filterChangeSet(changeSet);
		changeSet = this.flattenUpdates(changeSet);

		// Map the changeset to an ApiPayload
		const apiPayload = this.changeSetToApiPayload(changeSet);

		return this.http.post<ApiResponse<any>>(url, apiPayload).pipe(map(this.apiResponseToChangeSet));
	}

	changeSetToApiPayload<T>(changeSet: ChangeSet): ApiPayload<T, any[]> {
		const recordType: RecordTypeName<T> = changeSet.extras;
		const records = changeSet.changes.find((c) => c.entityName === recordType)?.entities as T[];
		const related = changeSet.changes
			.filter((c) => c.entityName !== recordType)
			.map((c) => {
				return {
					recordType: c.entityName,
					records: c.entities as any[],
					metadata: {} as any,
				} as ApiResponse<any>;
			});
		return {
			recordType,
			records,
			related,
		};
	}

	apiResponseToChangeSet = <T>(apiResponse: ApiResponse<T>): ChangeSet<RecordTypeName<T>> => {
		const changes: ChangeSetItem[] = [];

		// Convert main records to ChangeSetItems
		changes.push(this.apiPayloadToChangeSetItem(apiResponse));

		// Convert any related records to ChangeSetItems
		(apiResponse.related || []).forEach((related) =>
			changes.push(this.apiPayloadToChangeSetItem(related))
		);

		// Build ChangeSet
		const changeSet: ChangeSet = {
			changes,
		};
		return changeSet;
	};

	apiPayloadToChangeSetItem = <T>(apiPayload: ApiPayload<T>): ChangeSetItem => {
		return changeSetItemFactory.upsert(apiPayload.recordType, apiPayload.records);
	};
}
